import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Save, TrendingUp, Moon, Clock, Activity, Bookmark, Copy, ClipboardPaste, CalendarDays, Sparkles, Shield, ArrowUp, ArrowDown, UserCog, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loader, Empty, btn } from "@/components/Page";
import { SKILLS, SKILL_META } from "@/lib/game";
import { StatBars } from "@/components/StatBars";
import { StatAdvancesPanel } from "@/components/StatAdvancesPanel";
import { SkillsGuideButton, ClassSkillsGuideButton } from "@/components/CombatGuide";
import TraitSelectionModal from "@/components/TraitSelectionModal";
import { TraitDisplay } from "@/components/TraitDisplay";
import TraitAssignmentBanner from "@/components/TraitAssignmentBanner";

// Custom Training Icon
const TrainingIcon = ({ size = 24, className = "" }) => (
  <img 
    src="/training-icon.png" 
    alt="Training" 
    className={`inline-block ${className}`}
    style={{ 
      width: size * 1.2,
      height: size * 1.2,
      objectFit: 'contain'
    }}
  />
);

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const FULL_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const REST_DAY = 6;
const TRAINING_TABS = new Set(["schedule", "summary", "advances"]);

function boostCountdown(iso) {
  if (!iso) return "";
  const ms = new Date(iso) - Date.now();
  if (ms <= 0) return "ending soon";
  const d = Math.floor(ms / 86400000), h = Math.floor((ms % 86400000) / 3600000), m = Math.floor((ms % 3600000) / 60000);
  if (d > 0) return `${d}d ${h}h`;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export default function Training() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [active, setActive] = useState(null);
  const [rest, setRest] = useState(null);
  const [sel, setSel] = useState(null);
  const [sched, setSched] = useState([]);
  const [busy, setBusy] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const requested = searchParams.get("tab");
    return TRAINING_TABS.has(requested) ? requested : "schedule";
  });
  const [needsTraits, setNeedsTraits] = useState([]);
  const [traitModalGlad, setTraitModalGlad] = useState(null);
  const [clipboard, setClipboard] = useState(() => {
    try { return JSON.parse(localStorage.getItem("train_clipboard") || "null"); } catch { return null; }
  });

  useEffect(() => {
    api.get("/gladiators").then((r) => {
      setActive(r.data.active);
      setRest(r.data.inactivity_rest || null);
      const requestedGladiator = searchParams.get("gladiator");
      const selectedGladiator = r.data.active.find((gladiator) => gladiator.id === requestedGladiator) || r.data.active[0];
      if (selectedGladiator) pick(selectedGladiator);
    }).catch(() => setActive([]));
    
    // Fetch gladiators needing traits
    api.get("/gladiators/needs-traits").then((r) => {
      console.log('[TRAITS] API response:', r.data);
      setNeedsTraits(r.data.gladiators || []);
    }).catch((e) => {
      console.error('[TRAITS] API error:', e);
      setNeedsTraits([]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const requested = searchParams.get("tab");
    const nextTab = TRAINING_TABS.has(requested) ? requested : "schedule";
    setActiveTab((prev) => (prev === nextTab ? prev : nextTab));
  }, [searchParams]);

  const switchTab = (tab) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    if (tab === "schedule") next.delete("tab");
    else next.set("tab", tab);
    setSearchParams(next, { replace: true });
  };

  const toEditor = (src) => {
    const s = [];
    for (let i = 0; i < 7; i++) {
      const d = (src && src[i]) || [];
      s.push([d[0] || "", d[1] || "", d[2] || ""]);
    }
    return s;
  };

  const pick = (g) => {
    setSel(g);
    setSched(toEditor(g.training_schedule));
  };

  const setSlot = (day, idx, val) => {
    setSched((prev) => prev.map((d, i) => i === day ? d.map((v, j) => j === idx ? val : v) : d));
  };

  const currentPayload = () => sched.map((d) => [d[0] || "", d[1] || "", d[2] || ""]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/gladiators/${sel.id}/schedule`, { schedule: currentPayload() });
      toast.success("Training schedule updated");
      setActive((prev) => prev.map((g) => g.id === data.id ? data : g));
      setSel(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const slotLabel = (n) => sel?.schedule_slot_names?.[n] || `preset ${n + 1}`;

  const saveSlot = async (n) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/gladiators/${sel.id}/schedule-slots/${n}`, { schedule: currentPayload() });
      toast.success(`Saved to ${slotLabel(n)}`);
      setActive((prev) => prev.map((g) => g.id === data.id ? data : g));
      setSel(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const renameSlot = async (n, name) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/gladiators/${sel.id}/schedule-slots/${n}/name`, { name });
      setActive((prev) => prev.map((g) => g.id === data.id ? data : g));
      setSel(data);
      toast.success(name.trim() ? `Preset renamed to "${name.trim()}"` : `Preset ${n + 1} name cleared`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const loadSlot = (n) => {
    const preset = sel?.schedule_slots?.[n];
    if (!preset) return toast.error(`${slotLabel(n)} is empty`);
    setSched(toEditor(preset));
    toast.success(`Loaded ${slotLabel(n)} — review and Save Schedule to apply`);
  };

  const trainingLocked = !!sel && !["prime", "veteran"].includes(sel.lifecycle);

  const copySchedule = (schedule, label) => {
    const clean = (schedule || []).map((d) => [(d || [])[0] || "", (d || [])[1] || "", (d || [])[2] || ""]);
    setClipboard(clean);
    localStorage.setItem("train_clipboard", JSON.stringify(clean));
    toast.success(`Copied ${label} — switch fighter and paste`);
  };

  const pasteClipboard = () => {
    if (!clipboard) return toast.error("Clipboard is empty");
    setSched(toEditor(clipboard));
    toast.success("Pasted — review and Save Schedule to apply");
  };

  const handleAssignTraits = async (traits) => {
    if (!traitModalGlad) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/gladiators/${traitModalGlad.id}/assign-traits`, traits);
      // Update the gladiator in active list
      setActive((prev) => prev.map((g) => g.id === data.id ? data : g));
      if (sel?.id === data.id) setSel(data);
      // Remove from needs traits list
      setNeedsTraits((prev) => prev.filter((g) => g.id !== traitModalGlad.id));
      setTraitModalGlad(null);
      toast.success(`Traits assigned to ${data.name}!`);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const openTraitAssignment = () => {
    if (needsTraits.length === 0) return;
    // Find the first gladiator that needs traits from the active list
    const glad = active.find(g => needsTraits.some(n => n.id === g.id));
    if (glad) setTraitModalGlad(glad);
  };

  if (active === null) return <Loader />;
  if (!active.length) return (<div><PageHeader title="Training" icon={TrainingIcon} /><Empty label="Recruit a gladiator first to assign training." /></div>);

  return (
    <div>
      <div className="relative mb-6 overflow-hidden border border-amber-900/40 rounded-sm">
        <img src="/assets/ludus.jpg"
          alt="Ludus training grounds" data-testid="training-banner"
          className="w-full h-40 sm:h-52 object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-transparent" />
      </div>
      <PageHeader title="Weekly Training" subtitle="Pick up to 3 skills per day, Mon–Sat. Each day's 3 sessions run one every 8 hours in real time — so you get 3 training updates a day. Sunday is a forced rest day. Training runs even while you're offline." icon={TrainingIcon} />
      <p data-testid="training-push-line" className="-mt-3 mb-6 font-cinzel text-amber-400 uppercase tracking-wide text-sm">
        Gladiators are not allowed to rest! Push them!
      </p>

      {/* Trait Assignment Banner */}
      <TraitAssignmentBanner count={needsTraits.length} onClick={openTraitAssignment} />

      {rest && rest.away_days >= 14 && (
        <div data-testid="training-inactivity-notice" className="mb-6 flex items-start gap-3 border border-indigo-500/50 bg-indigo-950/25 px-4 py-3 rounded-sm">
          <Moon size={18} className="text-indigo-300 shrink-0 mt-0.5" />
          <p className="flex-1 text-sm text-indigo-100">
            <span className="font-cinzel uppercase tracking-wide text-indigo-300">Recent sessions show no gains — here's why.</span>
            {" "}You were away {rest.away_days} days, so your gladiators <span className="font-semibold">rested rather than trained</span>
            {rest.declined ? " and slipped slightly without your guidance" : " — no progress was lost, this is not a bug"}.
            {" "}Training resumes from the next session now that you're back.
          </p>
        </div>
      )}

      <div className="flex items-center gap-2 mb-6 border-b border-zinc-800">
        {[["schedule", "Schedule"], ["summary", "Training Summary"], ["advances", "Stat Advances"]].map(([id, label]) => (
          <button key={id} data-testid={`training-tab-${id}`} onClick={() => switchTab(id)}
            className={`px-4 py-2 text-sm font-cinzel uppercase tracking-wide border-b-2 -mb-px transition-colors ${
              activeTab === id ? "border-amber-500 text-amber-300" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
            {label}
          </button>
        ))}
      </div>

      {activeTab === "summary" && <TrainingSummary />}

      {activeTab === "advances" && (
        <div className="space-y-5">
          <div className="bg-zinc-950 border border-amber-900/30 p-4">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <h3 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm">All Gladiator Stat Advances</h3>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500">Newest changes first</div>
            </div>
            <p className="text-xs text-zinc-500 mb-4">Stat titles and Stat Advances are based on base private skills only. Gear, beasts, and temporary modifiers do not affect this history. This feed combines the most recent changes from all of your gladiators.</p>
            <StatAdvancesPanel />
          </div>
        </div>
      )}

      {activeTab === "schedule" && (<>
      <div className="flex flex-wrap gap-2 mb-6">
        {active.map((g) => (
          <button key={g.id} data-testid={`train-select-${g.id}`} onClick={() => pick(g)}
            className={`px-4 py-2 text-sm font-cinzel uppercase tracking-wide border transition-colors ${
              sel?.id === g.id ? "bg-amber-900/30 border-amber-600 text-amber-300" : "bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-amber-700/50"}`}>
            {g.name} <span className="text-zinc-600">· {g.age}y</span>
          </button>
        ))}
      </div>

      {sel && (
        <>
          {sel.newborn_boost?.active && (
            <div data-testid="newborn-boost-banner" className="mb-4 flex items-center gap-3 border border-emerald-700/50 bg-gradient-to-r from-emerald-950/40 to-zinc-950 px-4 py-3 rounded-sm">
              <Sparkles size={20} className="text-emerald-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-emerald-200 font-semibold">{sel.newborn_boost.mult}× Rookie Training Boost active for {sel.name}!</p>
                <p className="text-[12px] text-zinc-400">New gladiators train at <span className="text-emerald-300">double speed</span> for their first {sel.newborn_boost.days} days. Make the most of it — set an ambitious schedule below.</p>
              </div>
              <span data-testid="newborn-boost-countdown" className="shrink-0 flex items-center gap-1.5 text-sm text-emerald-300 font-cinzel tabular-nums whitespace-nowrap">
                <Clock size={14} /> {boostCountdown(sel.newborn_boost.until)} left
              </span>
            </div>
          )}

          {sel.campaign_boost?.active && (
            <div data-testid="campaign-boost-banner" className="mb-4 flex items-center gap-3 border border-emerald-700/50 bg-gradient-to-r from-emerald-950/40 to-zinc-950 px-4 py-3 rounded-sm">
              <TrendingUp size={20} className="text-emerald-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-emerald-200 font-semibold">Veteran's Edge: +{sel.campaign_boost.mult_pct}% Training active for {sel.name}!</p>
                <p className="text-[12px] text-zinc-400">A province-conquest reward — trains <span className="text-emerald-300">+{sel.campaign_boost.mult_pct}% faster</span> for {sel.campaign_boost.days} days. ({sel.campaign_boost.used}/{sel.campaign_boost.max} career boosts used)</p>
              </div>
              <span data-testid="campaign-boost-countdown" className="shrink-0 flex items-center gap-1.5 text-sm text-emerald-300 font-cinzel tabular-nums whitespace-nowrap">
                <Clock size={14} /> {boostCountdown(sel.campaign_boost.until)} left
              </span>
            </div>
          )}

          {sel.specialization && (
            <div data-testid="specialization-banner" className="mb-4 border border-amber-700/50 bg-gradient-to-r from-amber-950/40 to-zinc-950 px-4 py-3 rounded-sm">
              <div className="flex items-center gap-3 mb-2">
                <Shield size={20} className="text-amber-400 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-amber-200 font-semibold">Specialized Training by {sel.specialization.doctore_name}</p>
                  <p className="text-[12px] text-zinc-400">Permanent training modifications — {sel.specialization.doctore_tier} tier (±{Math.round(sel.specialization.bonus_pct * 100)}%)</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-green-950/30 border border-green-900/30 rounded px-3 py-2">
                  <div className="flex items-center gap-1.5 text-green-400 font-semibold mb-1">
                    <ArrowUp size={12} /> Boosted (+{Math.round(sel.specialization.bonus_pct * 100)}%)
                  </div>
                  <div className="text-zinc-400 capitalize">
                    {sel.specialization.boosts.join(', ')}
                  </div>
                </div>
                <div className="bg-red-950/30 border border-red-900/30 rounded px-3 py-2">
                  <div className="flex items-center gap-1.5 text-red-400 font-semibold mb-1">
                    <ArrowDown size={12} /> Penalized (−{Math.round(sel.specialization.bonus_pct * 100)}%)
                  </div>
                  <div className="text-zinc-400 capitalize">
                    {sel.specialization.penalties.join(', ')}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Mentor Banner */}
          {sel.doctore_mentor && (
            <div data-testid="mentor-banner" className="mb-4 border border-blue-700/50 bg-gradient-to-r from-blue-950/40 to-zinc-950 px-4 py-3 rounded-sm">
              <div className="flex items-center gap-3">
                <UserCog size={20} className="text-blue-400 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-blue-200 font-semibold">Personal Mentoring by {sel.doctore_mentor.doctore_name}</p>
                  <p className="text-[12px] text-zinc-400">Ongoing training bonus — {sel.doctore_mentor.doctore_tier} tier</p>
                </div>
              </div>
              <div className="mt-2 bg-blue-950/30 border border-blue-900/30 rounded px-3 py-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-zinc-400 capitalize">
                    <span className="text-blue-400 font-semibold">+{Math.round((sel.doctore_mentor.mentor_pct || 0) * 100)}%</span> to {sel.doctore_mentor.skill}
                  </span>
                  <span className="text-zinc-500 text-[11px]">Active training boost</span>
                </div>
              </div>
            </div>
          )}

          <div data-testid="training-stats" className="mb-4 bg-zinc-950 border border-amber-900/30 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm flex items-center gap-2">
                <Activity size={15} /> {sel.name}'s Current Base Stats
              </h3>
              <div className="flex items-center gap-3">
                <SkillsGuideButton />
                <ClassSkillsGuideButton />
                <span className="text-[11px] text-zinc-500 uppercase tracking-wide">
                  Overall <span className="text-amber-400 font-semibold tabular-nums">{sel.overall}</span>
                  {sel.peak_overall != null && sel.peak_overall !== sel.overall && (
                    <span className="text-zinc-600"> · Peak {sel.peak_overall}</span>
                  )}
                </span>
              </div>
            </div>
            <StatBars skills={sel.skills} skillTitles={sel.skill_titles} compact />
            <div data-testid="training-stat-title-note" className="mt-2 text-[11px] text-zinc-500 uppercase tracking-widest">
              Titles reflect base private skills only.
            </div>
            
            {/* Trait Display */}
            <TraitDisplay gladiator={sel} />
          </div>

          <div data-testid="training-lifecycle-status" className="mb-4 text-sm text-zinc-400 bg-zinc-950 border border-amber-900/30 px-4 py-3">
            {sel.lifecycle === "prime"
              ? <>
                  <div>In <span className="text-amber-300">prime</span> — skills grow each day.</div>
                  <div data-testid="training-prime-years-left" className="mt-1 text-xs text-zinc-500">
                    Years left in prime: {sel.prime_years_remaining == null
                      ? <span data-testid="training-prime-years-unscouted" className="text-zinc-400">Continue training your gladiator to reveal</span>
                      : <span data-testid="training-prime-years-value" className="tabular-nums text-amber-300">{Number(sel.prime_years_remaining).toFixed(1)}</span>}
                  </div>
                </>
              : sel.lifecycle === "veteran"
              ? <><span className="text-amber-300">Veteran</span> ({sel.age}y) — still training, but gains slow as your fighter approaches their prime's end (29y).</>
              : <>Past prime ({sel.age}y) — this gladiator is too far past his prime and can no longer gain skills.</>}
            <div data-testid="training-gladiator-class" className="mt-1.5 text-[11px] uppercase tracking-widest text-zinc-500">
              Class · <span className="text-amber-300">{sel.cls}</span>
            </div>
          </div>

          {!trainingLocked && sel.training_day && (
            <div data-testid="active-training-day" className="mb-4 flex items-center gap-2 bg-amber-950/20 border border-amber-800/40 px-4 py-2.5 text-sm">
              <CalendarDays size={16} className="text-amber-400 shrink-0" />
              {sel.training_day === "Sunday"
                ? <span className="text-zinc-300">Today is <span className="text-amber-300 font-semibold">Sunday</span> — a forced rest day. Your fighter recovers; training resumes Monday.</span>
                : <span className="text-zinc-300">Today is <span className="text-amber-300 font-semibold">{sel.training_day}</span> — running this day's plan below.</span>}
            </div>
          )}
          {trainingLocked ? (
            <div data-testid="training-disabled-notice" className="mt-6 border border-red-900/40 bg-red-950/15 px-4 py-4 rounded-sm">
              <div className="font-cinzel text-red-300 uppercase tracking-wide text-sm">Training Disabled</div>
              <p className="mt-2 text-sm text-zinc-300">This gladiator is too far past his prime and can no longer gain skills.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-7 gap-px bg-zinc-800 border border-amber-900/40">
                {DAYS.map((day, di) => {
                  const isToday = sel.training_day === FULL_DAYS[di];
                  return (
                  <div key={day} className={`p-3 min-h-[170px] flex flex-col gap-2 ${isToday ? "ring-1 ring-amber-500/70 bg-amber-950/10" : di === REST_DAY ? "bg-zinc-900/60" : "bg-zinc-950"}`}>
                    <div className={`font-cinzel text-xs uppercase tracking-widest text-center border-b border-zinc-800 pb-2 ${di === REST_DAY ? "text-zinc-500" : "text-amber-400"}`}>{day}{isToday && <span className="block text-[9px] text-amber-300 tracking-wider">● Today</span>}</div>
                    {di === REST_DAY ? (
                      <div data-testid="rest-day" className="flex-1 flex flex-col items-center justify-center text-center gap-2 text-zinc-600">
                        <Moon size={20} className="text-zinc-500" />
                        <span className="text-[11px] uppercase tracking-widest">Rest Day</span>
                        <span className="text-[10px] text-zinc-700 leading-snug">No training — gladiators recover.</span>
                      </div>
                    ) : (
                      [0, 1, 2].map((slot) => (
                        <select key={slot} data-testid={`schedule-${di}-${slot}`} value={sched[di]?.[slot] || ""}
                          onChange={(e) => setSlot(di, slot, e.target.value)}
                          className="bg-zinc-900 text-zinc-300 text-xs px-2 py-2 border border-zinc-800 focus:border-amber-600 outline-none">
                          <option value="">— rest —</option>
                          {SKILLS.map((s) => <option key={s} value={s}>{SKILL_META[s].label}</option>)}
                        </select>
                      ))
                    )}
                  </div>
                );})}
              </div>

              <button data-testid="save-schedule-btn" disabled={busy} onClick={save} className={btn() + " mt-6"}>
                <span className="flex items-center gap-2"><Save size={15} /> Save Schedule</span>
              </button>

              <div data-testid="schedule-presets" className="mt-6 bg-zinc-950 border border-amber-900/30 p-4">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <h3 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm flex items-center gap-2">
                    <Bookmark size={15} /> Schedule Presets
                  </h3>
                  <div className="flex items-center gap-2">
                    <button data-testid="copy-current-btn" onClick={() => copySchedule(currentPayload(), "current schedule")}
                      className="px-3 py-1.5 text-xs uppercase tracking-wide border border-zinc-700 text-zinc-300 hover:border-amber-600 hover:text-amber-300 transition-colors rounded-sm flex items-center gap-1.5">
                      <Copy size={13} /> Copy current
                    </button>
                    <button data-testid="paste-btn" onClick={pasteClipboard} disabled={!clipboard}
                      className="px-3 py-1.5 text-xs uppercase tracking-wide border border-zinc-700 text-zinc-300 hover:border-emerald-600 hover:text-emerald-300 transition-colors rounded-sm flex items-center gap-1.5 disabled:opacity-40">
                      <ClipboardPaste size={13} /> Paste{clipboard ? "" : " (empty)"}
                    </button>
                  </div>
                </div>
                <p className="text-[11px] text-zinc-500 mb-3">Save up to 3 schedules per fighter. Use Copy/Paste to transfer a schedule to another gladiator (then Save Schedule to apply). - You do not need to save to rename! Just type the name and click anywhere else on the screen, it will save.</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[0, 1, 2].map((n) => {
                    const filled = !!sel.schedule_slots?.[n];
                    const slotName = sel.schedule_slot_names?.[n] || "";
                    return (
                      <div key={n} data-testid={`preset-slot-${n}`} className={`border p-3 rounded-sm ${filled ? "border-amber-800/40 bg-amber-950/10" : "border-zinc-800 bg-zinc-900/40"}`}>
                        <div className="flex items-center justify-between mb-2 gap-2">
                          <input
                            data-testid={`preset-name-${n}`}
                            defaultValue={slotName}
                            key={slotName}
                            placeholder={`Preset ${n + 1}`}
                            maxLength={24}
                            onBlur={(e) => { if ((e.target.value || "") !== slotName) renameSlot(n, e.target.value); }}
                            onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                            className="min-w-0 flex-1 bg-transparent border-b border-transparent hover:border-zinc-700 focus:border-amber-600 outline-none text-xs uppercase tracking-widest text-amber-200 font-cinzel placeholder:text-zinc-500 placeholder:normal-case transition-colors"
                          />
                          <span className={`shrink-0 text-[10px] uppercase tracking-wide ${filled ? "text-amber-400" : "text-zinc-600"}`}>{filled ? "Saved" : "Empty"}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <button data-testid={`preset-save-${n}`} onClick={() => saveSlot(n)} disabled={busy}
                            className="flex-1 px-2 py-1.5 text-[11px] uppercase tracking-wide bg-amber-900/30 border border-amber-800/40 text-amber-200 hover:bg-amber-900/50 transition-colors rounded-sm disabled:opacity-50">Save</button>
                          <button data-testid={`preset-load-${n}`} onClick={() => loadSlot(n)} disabled={!filled}
                            className="flex-1 px-2 py-1.5 text-[11px] uppercase tracking-wide border border-zinc-700 text-zinc-300 hover:border-amber-600 hover:text-amber-300 transition-colors rounded-sm disabled:opacity-40">Load</button>
                          <button data-testid={`preset-copy-${n}`} onClick={() => copySchedule(sel.schedule_slots[n], slotLabel(n))} disabled={!filled} title="Copy to clipboard"
                            className="px-2 py-1.5 text-zinc-400 border border-zinc-700 hover:border-amber-600 hover:text-amber-300 transition-colors rounded-sm disabled:opacity-40"><Copy size={13} /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <DailySessions sessions={sel.daily_sessions} nextAt={sel.next_session_at} />
              <DailySessions sessions={sel.yesterday_sessions} title="Yesterday's Sessions"
                subtitle="What your gladiator trained over the previous game-day's 3 sessions."
                restMsg="No training recorded for yesterday."
                testid="yesterday-sessions" sessionPrefix="yesterday-session" />

              <div className="grid sm:grid-cols-2 gap-5 mt-8">
                <GainTracker testid="weekly-gains" title="This Week's Training" subtitle="Total gains over the active week (Monday-Sunday) - This will differ from the literal last 7 days on the training summary." data={sel.weekly_gains} signed />
                <GainTracker testid="lifetime-gains" title="Lifetime Gains" subtitle="Total skill points earned through training" data={sel.lifetime_gains} />
              </div>
            </>
          )}
        </>
      )}
      </>)}

      {/* Trait Selection Modal */}
      {traitModalGlad && (
        <TraitSelectionModal
          gladiator={traitModalGlad}
          onConfirm={handleAssignTraits}
          onClose={() => setTraitModalGlad(null)}
        />
      )}
    </div>
  );
}

function SessionCountdown({ iso }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  if (!iso) return null;
  const ms = new Date(iso) - Date.now();
  if (ms <= 0) return <span className="text-emerald-400 normal-case">running now…</span>;
  const h = Math.floor(ms / 3.6e6), m = Math.floor((ms % 3.6e6) / 6e4), s = Math.floor((ms % 6e4) / 1000);
  const txt = h > 0 ? `${h}h ${m}m ${s}s` : m > 0 ? `${m}m ${s}s` : `${s}s`;
  return <span className="tabular-nums text-amber-300">{txt}</span>;
}

function buildTrainingProgressBar(percent) {
  const clamped = Math.max(0, Math.min(100, Math.round(percent || 0)));
  const filled = Math.max(0, Math.min(10, Math.round(clamped / 10)));
  return `${"█".repeat(filled)}${"░".repeat(10 - filled)} ${clamped}%`;
}

function TrainingProgressStatus({ sessions, nextAt }) {
  const [, setTick] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  if (!nextAt) return null;

  const nextMs = new Date(nextAt).getTime();
  if (Number.isNaN(nextMs)) return null;

  const nowMs = Date.now();
  const sessionLengthMs = 8 * 60 * 60 * 1000;
  const sessionStartMs = nextMs - sessionLengthMs;
  const percent = ((nowMs - sessionStartMs) / sessionLengthMs) * 100;
  const progressBar = buildTrainingProgressBar(percent);
  const pendingSession = (sessions || []).find((session) => !session.done);
  const currentMeta = pendingSession?.skill ? SKILL_META[pendingSession.skill] : null;
  const currentLabel = currentMeta?.label || (pendingSession ? "Recovery" : "Conditioning");
  const currentSessionNumber = pendingSession?.session || null;

  return (
    <div data-testid="training-progress-status" className="flex flex-col items-start gap-1.5 border border-emerald-800/40 bg-emerald-950/20 px-3 py-2 rounded-sm">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] uppercase tracking-widest">
        <span data-testid="training-progress-label" className="font-cinzel text-emerald-300">Training in progress</span>
        <span data-testid="training-progress-skill" className="text-zinc-400 normal-case tracking-normal text-xs sm:text-[11px]">
          {currentSessionNumber ? `Session ${currentSessionNumber} · ` : ""}{currentLabel}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div data-testid="training-progress-visual" className="h-2 w-32 sm:w-40 overflow-hidden rounded-full bg-zinc-900 ring-1 ring-emerald-900/50">
          <div
            data-testid="training-progress-bar"
            className="h-full rounded-full bg-gradient-to-r from-emerald-500 via-emerald-400 to-amber-300 transition-[width] duration-700"
            style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
          />
        </div>
        <span data-testid="training-progress-bar-text" className="font-mono text-xs text-emerald-200">
          {progressBar}
        </span>
      </div>
      <span data-testid="next-session-countdown" className="text-[11px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5">
        <Clock size={11} className="text-amber-400" /> Next session in <SessionCountdown iso={nextAt} />
      </span>
    </div>
  );
}

function DailySessions({ sessions, nextAt, title = "Today's Sessions",
  subtitle = "Each day runs 3 training sessions — one every 8 hours in real time.",
  restMsg = "Rest day — gladiators recover, no sessions today.",
  testid = "daily-sessions", sessionPrefix = "daily-session" }) {
  return (
    <div data-testid={testid} className="bg-zinc-950 border border-amber-900/30 p-4 mt-8">
      <div className="flex items-center justify-between gap-2 mb-0.5 flex-wrap">
        <div className="flex items-center gap-2">
          <Clock size={16} className="text-emerald-500" />
          <h3 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm">{title}</h3>
        </div>
        {sessions && sessions.length > 0 && nextAt && (
          <TrainingProgressStatus sessions={sessions} nextAt={nextAt} />
        )}
      </div>
      <p className="text-[11px] text-zinc-500 mb-3">{subtitle}</p>
      {(!sessions || sessions.length === 0) ? (
        <p className="text-sm text-zinc-600 italic flex items-center gap-2"><Moon size={14} /> {restMsg}</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {sessions.map((s) => {
            const meta = s.skill ? SKILL_META[s.skill] : null;
            const Icon = meta?.icon;
            return (
              <div key={s.session} data-testid={`${sessionPrefix}-${s.session}`}
                className={`border p-3 rounded-sm ${s.done ? "border-emerald-700/40 bg-emerald-950/15" : "border-zinc-800 bg-zinc-900/40"}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500">Session {s.session}</span>
                  <span className={`text-[10px] uppercase tracking-wide ${s.done ? "text-emerald-400" : "text-zinc-600"}`}>{s.done ? "Done" : "Pending"}</span>
                </div>
                {meta ? (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-zinc-300 flex items-center gap-1.5">{Icon && <Icon size={13} className="opacity-70" />}{meta.label}</span>
                    <span className={`tabular-nums font-semibold text-sm ${s.done && s.gain > 0 ? "text-emerald-400" : "text-zinc-600"}`}>{s.done && s.gain > 0 ? `+${s.gain}` : "—"}</span>
                  </div>
                ) : (
                  <span className="text-sm text-zinc-600 italic">Rest</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function GainTracker({ title, subtitle, data, signed, testid }) {
  const total = SKILLS.reduce((a, s) => a + (data?.[s] || 0), 0);
  return (
    <div data-testid={testid} className="bg-zinc-950 border border-amber-900/30 p-4">
      <div className="flex items-center gap-2 mb-0.5">
        <TrendingUp size={16} className="text-amber-500" />
        <h3 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm">{title}</h3>
      </div>
      <p className="text-[11px] text-zinc-500 mb-3">{subtitle}</p>
      <div className="space-y-1.5">
        {SKILLS.map((s) => {
          const v = data?.[s] || 0;
          const color = v > 0 ? "text-emerald-400" : v < 0 ? "text-orange-400" : "text-zinc-600";
          return (
            <div key={s} className="flex justify-between text-sm">
              <span className="text-zinc-400 flex items-center gap-1.5">{(() => { const I = SKILL_META[s].icon; return <I size={12} className="opacity-70" />; })()}{SKILL_META[s].label}</span>
              <span className={`tabular-nums font-medium ${color}`}>{signed && v > 0 ? "+" : ""}{v}</span>
            </div>
          );
        })}
        <div className="flex justify-between text-sm border-t border-zinc-800 pt-1.5 mt-1.5">
          <span className="text-zinc-300 uppercase text-[11px] tracking-widest">Total</span>
          <span className={`tabular-nums font-semibold ${total > 0 ? "text-emerald-400" : total < 0 ? "text-orange-400" : "text-zinc-500"}`}>{signed && total > 0 ? "+" : ""}{total}</span>
        </div>
      </div>
    </div>
  );
}


const PERIOD_LABELS = { today: "Today", yesterday: "Yesterday", last_7d: "Last 7 Days", last_14d: "Last 14 Days" };
const PERIOD_ORDER = ["today", "yesterday", "last_7d", "last_14d"];

function gainCls(v) {
  return v > 0 ? "text-emerald-400" : v < 0 ? "text-orange-400" : "text-zinc-600";
}
function fmtGain(v) {
  return v > 0 ? `+${v}` : `${v}`;
}

function scrollToElement(targetId) {
  if (typeof document === "undefined") return;
  const element = document.getElementById(targetId);
  if (!element) return;
  element.scrollIntoView({ behavior: "smooth", block: "start" });
}

function TrainingSummary() {
  const [data, setData] = useState(null);
  const [traitModalGlad, setTraitModalGlad] = useState(null);
  const [sortBy, setSortBy] = useState("last_7d"); // Default sort by last 7 days

  const handleAssignTraits = async (traits) => {
    if (!traitModalGlad) return;
    try {
      await api.post(`/gladiators/${traitModalGlad.id}/assign-traits`, traits);
      toast.success(`Traits assigned to ${traitModalGlad.name}!`);
      setTraitModalGlad(null);
      // Reload the summary to reflect changes
      api.get("/training/summary").then((r) => setData(r.data)).catch(() => setData(false));
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  useEffect(() => {
    api.get("/training/summary").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  if (data === null) return <Loader />;
  if (!data || !data.fighters?.length) return <Empty label="No active gladiators to summarize. Recruit and train a fighter first." />;

  // Sort fighters by the selected period (highest gains first)
  const sortedFighters = [...data.fighters].sort((a, b) => {
    return b.period_totals[sortBy] - a.period_totals[sortBy];
  });

  return (
    <div data-testid="training-summary" className="space-y-6">
      <div className="flex items-start justify-between gap-4 -mt-2">
        <p className="text-sm text-zinc-400">
          Net skill points gained (training) per fighter, at a glance. Values reflect actual training after age taper, rest days and boosts.
        </p>
        
        {/* Sort By Dropdown */}
        <div className="flex items-center gap-2 min-w-fit">
          <ArrowUpDown size={14} className="text-zinc-500" />
          <span className="text-xs text-zinc-500 uppercase tracking-wider whitespace-nowrap">Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-zinc-900 border border-zinc-800 text-zinc-300 text-xs px-2 py-1.5 focus:border-amber-600 outline-none cursor-pointer"
          >
            <option value="today">Most gained today</option>
            <option value="yesterday">Most gained yesterday</option>
            <option value="last_7d">Most gained last 7 days</option>
            <option value="last_14d">Most gained last 14 days</option>
          </select>
        </div>
      </div>

      {/* Ludus-wide roll-up */}
      <div id="summary-ludus-total" data-testid="summary-ludus-total" className="bg-zinc-950 border border-amber-800/40 rounded-sm p-4 scroll-mt-24">
        <h3 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm flex items-center gap-2 mb-3">
          <TrendingUp size={15} /> Ludus Total ({data.fighters.length} fighter{data.fighters.length === 1 ? "" : "s"})
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {PERIOD_ORDER.map((p) => {
            const total = data.grand_totals[p];
            return (
              <div key={p} data-testid={`ludus-total-${p}`} className="bg-zinc-900/50 border border-zinc-800 rounded-sm px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">{PERIOD_LABELS[p]}</div>
                <div className={`font-cinzel text-2xl tabular-nums ${gainCls(total)}`}>{fmtGain(total)}</div>
              </div>
            );
          })}
        </div>
        <div data-testid="training-summary-jump-links" className="mt-4 border border-zinc-800 bg-zinc-900/50 rounded-sm px-3 py-3 space-y-2">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Jump to</div>
          <div className="flex flex-wrap gap-2">
            {sortedFighters.map((f) => (
              <button
                key={f.glad_id}
                type="button"
                data-testid={`training-summary-jump-button-${f.glad_id}`}
                onClick={() => scrollToElement(`training-summary-fighter-anchor-${f.glad_id}`)}
                className="px-2.5 py-1 border border-amber-700/50 bg-amber-950/15 text-amber-200 hover:bg-amber-950/30 rounded-sm text-[10px] sm:text-[11px] uppercase tracking-[0.18em] transition-colors"
              >
                {f.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Per-fighter breakdowns */}
      {sortedFighters.map((f) => (
        <div id={`training-summary-fighter-anchor-${f.glad_id}`} key={f.glad_id} data-testid={`summary-fighter-${f.glad_id}`} className="bg-zinc-950 border border-amber-900/30 rounded-sm overflow-hidden scroll-mt-24">
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-zinc-800 bg-zinc-900/40">
            <div className="min-w-0">
              <h4 className="font-cinzel text-amber-200 uppercase tracking-wide text-sm break-words">
                {f.name} <span className="text-zinc-600 normal-case tracking-normal text-xs">· {f.cls} · {f.age}y</span>
              </h4>
              <button
                type="button"
                data-testid={`training-summary-return-top-${f.glad_id}`}
                onClick={() => scrollToElement("summary-ludus-total")}
                className="mt-1 text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-amber-300 transition-colors"
              >
                Return to top ↑
              </button>
            </div>
            <span className="text-[11px] uppercase tracking-widest text-zinc-500 shrink-0 text-right">
              {PERIOD_LABELS[sortBy]} <span className={`font-semibold tabular-nums ${gainCls(f.period_totals[sortBy])}`}>{fmtGain(f.period_totals[sortBy])}</span>
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                  <th className="text-left font-normal px-4 py-2">Skill</th>
                  {PERIOD_ORDER.map((p) => (
                    <th key={p} className="text-right font-normal px-4 py-2">{PERIOD_LABELS[p]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SKILLS.map((s) => {
                  const I = SKILL_META[s].icon;
                  return (
                    <tr key={s} data-testid={`summary-${f.glad_id}-${s}`} className="border-t border-zinc-900">
                      <td className="px-4 py-1.5">
                        <span className="flex items-center gap-1.5 text-zinc-400"><I size={12} className="opacity-70" />{SKILL_META[s].label}</span>
                      </td>
                      {PERIOD_ORDER.map((p) => {
                        const v = f.periods[p][s];
                        return <td key={p} className={`text-right px-4 py-1.5 tabular-nums ${gainCls(v)}`}>{v === 0 ? "—" : fmtGain(v)}</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-zinc-800 bg-zinc-900/30">
                  <td className="px-4 py-2 text-[11px] uppercase tracking-widest text-zinc-300">Total</td>
                  {PERIOD_ORDER.map((p) => (
                    <td key={p} data-testid={`summary-${f.glad_id}-total-${p}`} className={`text-right px-4 py-2 tabular-nums font-semibold ${gainCls(f.period_totals[p])}`}>
                      {fmtGain(f.period_totals[p])}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      ))}

      {/* Trait Selection Modal */}
      {traitModalGlad && (
        <TraitSelectionModal
          gladiator={traitModalGlad}
          onConfirm={handleAssignTraits}
          onClose={() => setTraitModalGlad(null)}
        />
      )}
    </div>
  );
}
